import {makeAutoObservable, observable, runInAction} from 'mobx';
import StudioRepository from '../../../data/repositories/StudioRepository';
import {readMotionDesign} from '../motionDesign';
import type {
  MotionDesignOptions,
  StudioEstimates,
  StudioFont,
  StudioFontCategory,
} from '../types';
import {studioRequest} from './studioRequests';

export interface StudioCapabilitiesValue {
  canApproveOverride?: boolean;
  canResolveSme?: boolean;
  /**
   * Authors see the source checks and confirmations (the server's REVIEW_CHECKS). Off for now,
   * and until the options are read: confirmations and quoted-step overrides do not show.
   */
  reviewChecks?: boolean;
  [key: string]: unknown;
}

export interface StudioOptions {
  enforceAiCredits?: boolean;
  capabilities?: StudioCapabilitiesValue;
  canApproveOverride?: boolean;
  canResolveSme?: boolean;
  reviewChecks?: boolean;
  estimates?: Partial<StudioEstimates> | null;
  /** The fonts a video can use; older servers send none. */
  fonts?: unknown;
  /** The motion choices the server offers; older servers send none. */
  motionDesign?: unknown;
}

/** The server's planner rates, if it sends whole ones; older servers send none. */
export function readEstimates(value: StudioOptions['estimates']) {
  const number = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
  return value &&
    number(value.planTokensPerVideoSecond) &&
    number(value.plannerTokensPerSecond) &&
    number(value.sampleSize)
    ? (value as StudioEstimates)
    : null;
}

const FONT_CATEGORIES: ReadonlySet<unknown> = new Set<StudioFontCategory>([
  'sans',
  'serif',
  'display',
  'handwriting',
  'monospace',
]);

/**
 * The fonts the server lists, in its order, keeping only whole ones; null when it lists none
 * (older servers), so the branding panel keeps its font read-only.
 */
export function readFonts(value: unknown): StudioFont[] | null {
  if (!Array.isArray(value)) return null;
  const text = (v: unknown) => typeof v === 'string' && v.trim() !== '';
  const fonts = value
    .filter(
      (font): font is StudioFont =>
        !!font &&
        typeof font === 'object' &&
        text(font.id) &&
        text(font.name) &&
        FONT_CATEGORIES.has(font.category),
    )
    .map(({id, name, category}) => ({id, name, category}));
  return fonts.length > 0 ? fonts : null;
}

/**
 * What Studio offers in an organization (GET /options): the capabilities that show privileged
 * actions, the planner rates the time estimates use, the fonts a video can use and the motion
 * design changes a reel takes. Read once
 * per organization, so polling a job never asks for them again.
 *
 * Registered in storesProvider as `studioCapabilitiesStore`; CapabilitiesProvider, around
 * every Studio page, loads it and shares what it read. It outlives the pages, so it forgets an
 * organization's options when they close: the next Studio visit never starts from them.
 */
export default class CapabilitiesStore {
  capabilities: StudioCapabilitiesValue = {};
  estimates: StudioEstimates | null = null;
  /** null until read, or when the server lists none. */
  fonts: StudioFont[] | null = null;
  /** The motion choices and changes the server offers; null until read, or when it offers none. */
  motionDesign: MotionDesignOptions | null = null;

  private repository = new StudioRepository();
  private request = 0;

  constructor() {
    makeAutoObservable<CapabilitiesStore, 'repository' | 'request'>(this, {
      capabilities: observable.ref,
      estimates: observable.ref,
      fonts: observable.ref,
      motionDesign: observable.ref,
      repository: false,
      request: false,
    });
  }

  /**
   * Forgets the previous organization's options and reads this one's. Returns the function
   * that closes them (see `close`), for an effect's cleanup.
   */
  load(organizationId: string | undefined) {
    const current = ++this.request;
    this.forget();
    studioRequest(organizationId, org =>
      this.repository.options<StudioOptions>(org),
    )
      .then(options => {
        if (current !== this.request) return;
        runInAction(() => {
          this.capabilities = {
            ...options.capabilities,
            enforceAiCredits: options.enforceAiCredits !== false,
            canApproveOverride:
              options.canApproveOverride === true ||
              options.capabilities?.canApproveOverride === true,
            canResolveSme:
              options.canResolveSme === true ||
              options.capabilities?.canResolveSme === true,
            reviewChecks:
              options.reviewChecks === true ||
              options.capabilities?.reviewChecks === true,
          };
          this.estimates = readEstimates(options.estimates);
          this.fonts = readFonts(options.fonts);
          this.motionDesign = readMotionDesign(options.motionDesign);
        });
      })
      .catch(() => {
        /* Keep privileged actions hidden if capabilities are unavailable. */
      });
    return () => {
      if (this.request === current) this.close();
    };
  }

  /**
   * Ignores the answer still on its way and forgets the options read, e.g. when the Studio
   * pages close or on sign-out, so the next visit starts with privileged actions hidden.
   */
  close() {
    this.request += 1;
    this.forget();
  }

  private forget() {
    this.capabilities = {};
    this.estimates = null;
    this.fonts = null;
    this.motionDesign = null;
  }
}
